import Vue from 'vue'
import Vuex from 'vuex'
import Cookies from 'js-cookie'

// 创建并配置axios实例
const axios = require('axios')

// 确保axios实例有效
if (!axios) {
  console.error('axios is undefined!')
}

// 配置axios
axios.defaults.baseURL = process.env.VUE_APP_API_BASE_URL || '/api'
axios.defaults.headers.common['Content-Type'] = 'application/json'

// 请求拦截器，添加 token
axios.interceptors.request.use(config => {
  const token = Cookies.get('token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
}, error => {
  return Promise.reject(error)
})

// 响应拦截器，处理错误
axios.interceptors.response.use(response => {
  return response
}, error => {
  if (error.response && error.response.status === 401) {
    // 清除 token
    Cookies.remove('token')
    // 跳转到登录页，使用window.location.href确保能跳转
    window.location.href = '/login'
  }
  return Promise.reject(error)
})

Vue.use(Vuex)

export default new Vuex.Store({
  state: {
    user: null,
    token: null,
    isLoggedIn: false,
    projects: [],
    currentProject: null,
    databases: []
  },
  mutations: {
    SET_USER(state, user) {
      state.user = user
      state.isLoggedIn = !!user
    },
    SET_TOKEN(state, token) {
      state.token = token
      axios.defaults.headers.Authorization = token ? `Bearer ${token}` : ''
    },
    LOGOUT(state) {
      state.user = null
      state.token = null
      state.isLoggedIn = false
      state.projects = []
      state.currentProject = null
      state.databases = []
    },
    SET_PROJECTS(state, projects) {
      state.projects = projects
    },
    SET_CURRENT_PROJECT(state, project) {
      state.currentProject = project
    },
    SET_DATABASES(state, databases) {
      state.databases = databases
    }
  },
  actions: {
    // 登录
    async login({ commit }, credentials) {
      const response = await axios.post('/auth/login', credentials)
      const { access_token, user } = response.data
      
      // 保存 token 和用户信息到 cookies（30天过期）
      Cookies.set('token', access_token, { expires: 30 })
      Cookies.set('user', JSON.stringify(user), { expires: 30 })
      
      // 更新状态
      commit('SET_TOKEN', access_token)
      commit('SET_USER', user)
      
      return response
    },
    
    // 注册
    async register({ commit }, userData) {
      const response = await axios.post('/auth/register', userData)
      const { access_token, user } = response.data
      
      // 保存 token 和用户信息到 cookies（30天过期）
      Cookies.set('token', access_token, { expires: 30 })
      Cookies.set('user', JSON.stringify(user), { expires: 30 })
      
      // 更新状态
      commit('SET_TOKEN', access_token)
      commit('SET_USER', user)
      
      return response
    },
    
    // 退出登录
    logout({ commit }) {
      Cookies.remove('token')
      Cookies.remove('user')
      commit('LOGOUT')
    },
    
    // 初始化认证状态
    async initAuth({ commit }) {
      const token = Cookies.get('token')
      
      if (token) {
        commit('SET_TOKEN', token)
        
        // 从服务器获取最新的用户信息，而不是从 Cookie 读取
        try {
          const response = await axios.post('/auth/me')
          const user = response.data
          commit('SET_USER', user)
          // 更新 Cookie 中的用户信息（30天过期）
          Cookies.set('user', JSON.stringify(user), { expires: 30 })
        } catch (error) {
          console.error('获取用户信息失败:', error)
          // 如果获取失败，清除认证状态
          commit('LOGOUT')
          Cookies.remove('token')
          Cookies.remove('user')
        }
      }
    },
    
    // 获取用户项目列表
    async fetchProjects({ commit }) {
      try {
        // 使用直接导入的axios实例
        const response = await axios.get('/projects')
        commit('SET_PROJECTS', response.data)
        return response.data || []
      } catch (error) {
        console.error('获取项目列表失败:', error)
        commit('SET_PROJECTS', [])
        return []
      }
    },
    
    // 获取项目的数据库列表
    async fetchDatabases({ commit }, projectId) {
      try {
        // 使用直接导入的axios实例
        const response = await axios.get(`/projects/${projectId}/databases`)
        commit('SET_DATABASES', response.data)
        return response.data || []
      } catch (error) {
        console.error('获取数据库列表失败:', error)
        commit('SET_DATABASES', [])
        return []
      }
    }
  },
  getters: {
    isLoggedIn: state => state.isLoggedIn,
    currentUser: state => state.user,
    projects: state => state.projects,
    currentProject: state => state.currentProject,
    databases: state => state.databases,
    // 检查用户是否有写权限（管理员和VIP用户）
    canWrite: state => {
      const user = state.user
      return user && (user.level === '00' || user.level === '01')
    },
    // 检查用户是否是只读用户
    isReadOnly: state => {
      const user = state.user
      return user && user.level === '02'
    }
  }
})
